import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, Printer, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChallanPage = ({ user, token }) => {
  const [challans, setChallans] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [formData, setFormData] = useState({
    challan_number: "",
    vendor_name: "",
    material_type: "",
    weight: "",
    vehicle_number: "",
    purpose: "",
    from_address: "",
    to_address: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchHubs();
    fetchChallans();
    fetchMaterialTypes();
  }, [selectedHub]);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
        setFormData(prev => ({ ...prev, from_address: response.data[0].location }));
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await axios.get(`${API}/material-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterialTypes(response.data);
    } catch (error) {
      console.error("Error fetching material types:", error);
    }
  };

  const fetchChallans = async () => {
    try {
      const response = await axios.get(`${API}/challans`, {
        headers: { Authorization: `Bearer ${token}` },
        params: selectedHub ? { hub_id: selectedHub } : {}
      });
      setChallans(response.data);
    } catch (error) {
      console.error("Error fetching challans:", error);
    }
  };

  const handleCreateChallan = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dispatchResponse = await axios.post(
        `${API}/dispatch`,
        {
          hub_id: selectedHub,
          material_type: formData.material_type,
          weight: parseFloat(formData.weight),
          vendor_name: formData.vendor_name,
          vehicle_number: formData.vehicle_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await axios.post(
        `${API}/challans`,
        {
          dispatch_id: dispatchResponse.data.id,
          challan_number: formData.challan_number,
          purpose: formData.purpose,
          from_address: formData.from_address,
          to_address: formData.to_address,
          notes: formData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Challan created!");
      setFormData({
        challan_number: "",
        vendor_name: "",
        material_type: "",
        weight: "",
        vehicle_number: "",
        purpose: "",
        from_address: getCurrentHub()?.location || "",
        to_address: "",
        notes: ""
      });
      setIsDialogOpen(false);
      fetchChallans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const viewChallan = async (challanId) => {
    try {
      const response = await axios.get(`${API}/challans/${challanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedChallan(response.data);
    } catch (error) {
      toast.error("Failed to load challan");
    }
  };

  const getCurrentHub = () => hubs.find(h => h.id === selectedHub);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="challan-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Challan</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">Delivery challans</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 w-full sm:w-auto bg-gradient-to-r from-primary to-secondary hover:opacity-90" data-testid="create-challan-button">
              <Plus className="w-4 h-4" />
              Create
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-heading">Create Challan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateChallan} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Challan No.</Label>
                  <Input
                    data-testid="challan-number-input"
                    placeholder="CH-2024-001"
                    value={formData.challan_number}
                    onChange={(e) => setFormData({ ...formData, challan_number: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Vendor</Label>
                  <Input
                    data-testid="challan-vendor-input"
                    placeholder="Vendor name"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Material</Label>
                  <Select
                    value={formData.material_type}
                    onValueChange={(value) => setFormData({ ...formData, material_type: value })}
                  >
                    <SelectTrigger data-testid="challan-material-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {materialTypes.map((m) => (
                        <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Weight (kg)</Label>
                  <Input
                    data-testid="challan-weight-input"
                    type="number"
                    step="0.01"
                    placeholder="Weight"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Vehicle Number</Label>
                <Input
                  data-testid="challan-vehicle-input"
                  placeholder="MH-01-AB-1234"
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                  className="h-9"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Purpose</Label>
                <Input
                  data-testid="challan-purpose-input"
                  placeholder="Purpose"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="h-9"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">From Address</Label>
                  <Textarea
                    data-testid="challan-from-input"
                    placeholder="Hub address"
                    value={formData.from_address || getCurrentHub()?.location || ''}
                    onChange={(e) => setFormData({ ...formData, from_address: e.target.value })}
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">To Address</Label>
                  <Textarea
                    data-testid="challan-to-input"
                    placeholder="Destination"
                    value={formData.to_address}
                    onChange={(e) => setFormData({ ...formData, to_address: e.target.value })}
                    rows={2}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Notes (Optional)</Label>
                <Textarea
                  data-testid="challan-notes-input"
                  placeholder="Additional notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <Button type="submit" className="w-full h-9 bg-gradient-to-r from-primary to-secondary hover:opacity-90" disabled={loading} data-testid="submit-challan-button">
                {loading ? "Creating..." : "Create Challan"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="shadow-lg border-border/50 print:hidden">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">All Challans</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table data-testid="challans-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">No.</TableHead>
                  <TableHead className="text-xs sm:text-sm">Date</TableHead>
                  <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Vendor</TableHead>
                  <TableHead className="text-xs sm:text-sm">Material</TableHead>
                  <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Weight</TableHead>
                  <TableHead className="text-xs sm:text-sm text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {challans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground text-sm">
                      No challans yet
                    </TableCell>
                  </TableRow>
                ) : (
                  challans.slice(0, 10).map((challan) => (
                    <TableRow key={challan.id}>
                      <TableCell className="font-medium text-xs sm:text-sm">{challan.challan_number}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{challan.dispatch_data?.date}</TableCell>
                      <TableCell className="text-xs sm:text-sm hidden sm:table-cell">{challan.dispatch_data?.vendor_name}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{challan.dispatch_data?.material_type}</TableCell>
                      <TableCell className="text-xs sm:text-sm hidden sm:table-cell">{challan.dispatch_data?.weight}kg</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewChallan(challan.id)}
                          data-testid={`view-challan-${challan.id}`}
                          className="h-7 w-7 p-0"
                        >
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {selectedChallan && (
        <Card className="shadow-lg border-border/50 print:shadow-none" ref={printRef}>
          <CardHeader className="border-b bg-gradient-to-r from-green-50 via-white to-blue-50 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
              <div className="flex items-center gap-3 sm:gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hub-dispatch-mgmt/artifacts/ncecubvs_rr%20logo.jpg" 
                  alt="Recompose Logo" 
                  className="w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain"
                />
                <div>
                  <h2 className="text-lg sm:text-2xl lg:text-3xl font-heading font-bold text-primary">DELIVERY CHALLAN</h2>
                  <p className="text-muted-foreground text-xs sm:text-sm mt-1">Recompose Recycling</p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs sm:text-sm font-medium">Challan No:</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-primary">{selectedChallan.challan_number}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 sm:pt-6 space-y-4 sm:space-y-6 p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
              <div>
                <h3 className="font-heading font-semibold mb-2 sm:mb-3 text-sm sm:text-base lg:text-lg text-primary">From (Hub)</h3>
                <div className="bg-gradient-to-r from-green-50 to-white p-3 sm:p-4 rounded-lg border border-border">
                  <p className="text-xs sm:text-sm whitespace-pre-line">{selectedChallan.from_address}</p>
                </div>
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-2 sm:mb-3 text-sm sm:text-base lg:text-lg text-secondary">To (Destination)</h3>
                <div className="bg-gradient-to-r from-blue-50 to-white p-3 sm:p-4 rounded-lg border border-border">
                  <p className="text-xs sm:text-sm whitespace-pre-line">{selectedChallan.to_address}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
              <div className="bg-gradient-to-br from-green-50 to-white p-3 sm:p-4 rounded-lg border border-border">
                <h3 className="font-heading font-semibold mb-1 sm:mb-2 text-xs sm:text-sm text-primary">Date & Time</h3>
                <div className="space-y-1 text-xs sm:text-sm">
                  <p><span className="text-muted-foreground">Date:</span> {selectedChallan.dispatch_data?.date}</p>
                  <p><span className="text-muted-foreground">Time:</span> {new Date(selectedChallan.created_at).toLocaleTimeString()}</p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white p-3 sm:p-4 rounded-lg border border-border">
                <h3 className="font-heading font-semibold mb-1 sm:mb-2 text-xs sm:text-sm text-secondary">Vendor</h3>
                <div className="space-y-1 text-xs sm:text-sm">
                  <p><span className="text-muted-foreground">Name:</span> {selectedChallan.dispatch_data?.vendor_name}</p>
                  <p><span className="text-muted-foreground">Vehicle:</span> {selectedChallan.dispatch_data?.vehicle_number}</p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 via-white to-blue-50 p-3 sm:p-4 rounded-lg border border-border">
                <h3 className="font-heading font-semibold mb-1 sm:mb-2 text-xs sm:text-sm">Material</h3>
                <div className="space-y-1 text-xs sm:text-sm">
                  <p><span className="text-muted-foreground">Type:</span> {selectedChallan.dispatch_data?.material_type}</p>
                  <p><span className="text-muted-foreground">Weight:</span> {selectedChallan.dispatch_data?.weight} kg</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold mb-1 sm:mb-2 text-xs sm:text-sm">Purpose</h3>
              <p className="text-xs sm:text-sm bg-gradient-to-r from-green-50 via-white to-blue-50 p-3 sm:p-4 rounded-lg border border-border">{selectedChallan.purpose}</p>
            </div>

            {selectedChallan.notes && (
              <div>
                <h3 className="font-heading font-semibold mb-1 sm:mb-2 text-xs sm:text-sm">Notes</h3>
                <p className="text-xs sm:text-sm text-muted-foreground bg-muted/30 p-3 sm:p-4 rounded-lg">{selectedChallan.notes}</p>
              </div>
            )}

            <div className="border-t pt-4 sm:pt-6 mt-4 sm:mt-6">
              <div className="grid grid-cols-3 gap-4 sm:gap-8">
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-6 sm:mb-8">Prepared By</p>
                  <div className="border-t border-foreground/20 pt-1 sm:pt-2">
                    <p className="text-[10px] sm:text-xs font-medium">Signature</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-6 sm:mb-8">Driver</p>
                  <div className="border-t border-foreground/20 pt-1 sm:pt-2">
                    <p className="text-[10px] sm:text-xs font-medium">Signature</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-6 sm:mb-8">Receiver</p>
                  <div className="border-t border-foreground/20 pt-1 sm:pt-2">
                    <p className="text-[10px] sm:text-xs font-medium">Signature</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 print:hidden">
              <Button onClick={handlePrint} className="gap-2 bg-gradient-to-r from-primary to-secondary hover:opacity-90 h-9" data-testid="print-challan-button">
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button variant="outline" onClick={() => setSelectedChallan(null)} className="h-9">
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChallanPage;
